// Player customization configuration

// Character styles with unique shapes and features
// All use sphere bodies with different proportions for a rounder, cuter look
export const CHARACTER_STYLES = {
    creature: {
        id: 'creature',
        name: 'Creature',
        description: 'Round and cute with pointy ears',
        body: { type: 'sphere', scale: { x: 1.0, y: 1.1, z: 0.9 } },
        features: {
            belly: true,
            ears: { type: 'pointed', angle: 25 },
            tail: { type: 'round' },
            cheeks: true
        }
    },
    knight: {
        id: 'knight',
        name: 'Knight',
        description: 'Armored warrior with helmet',
        body: { type: 'sphere', scale: { x: 0.95, y: 1.0, z: 0.9 } },
        features: {
            belly: false,
            helmet: true,
            visor: true,
            plume: true
        }
    },
    dragon: {
        id: 'dragon',
        name: 'Dragon',
        description: 'Fierce with horns and wings',
        body: { type: 'sphere', scale: { x: 1.0, y: 1.0, z: 1.05 } },
        features: {
            belly: true,
            tail: { type: 'spiked' },
            horns: true,
            spikes: true,
            wings: true
        }
    },
    slime: {
        id: 'slime',
        name: 'Slime',
        description: 'Bouncy blob with a happy face',
        body: { type: 'sphere', scale: { x: 1.15, y: 0.85, z: 1.15 } },
        features: {
            belly: false,
            cheeks: true,
            droplets: true,
            shine: true
        }
    },
    robot: {
        id: 'robot',
        name: 'Robot',
        description: 'Mechanical with antenna',
        body: { type: 'sphere', scale: { x: 0.9, y: 1.0, z: 0.85 } },
        features: {
            belly: false,
            antenna: true,
            visor: true,
            panels: true
        }
    },
    cat: {
        id: 'cat',
        name: 'Cat',
        description: 'Feline friend with whiskers',
        body: { type: 'sphere', scale: { x: 0.95, y: 1.0, z: 0.9 } },
        features: {
            belly: true,
            ears: { type: 'cat', angle: 20 },
            tail: { type: 'long' },
            cheeks: true,
            whiskers: true,
            nose: true
        }
    }
};

// Color palettes for characters
export const COLOR_PALETTES = {
    golden: {
        id: 'golden',
        name: 'Golden',
        primary: { r: 1.0, g: 0.85, b: 0.2 },
        secondary: { r: 1.0, g: 0.95, b: 0.8 },
        accent: { r: 1.0, g: 0.4, b: 0.4 },
        dark: { r: 0.15, g: 0.1, b: 0.05 }
    },
    blue: {
        id: 'blue',
        name: 'Ocean',
        primary: { r: 0.3, g: 0.7, b: 1.0 },
        secondary: { r: 0.8, g: 0.9, b: 1.0 },
        accent: { r: 0.4, g: 0.8, b: 1.0 },
        dark: { r: 0.1, g: 0.2, b: 0.4 }
    },
    crimson: {
        id: 'crimson',
        name: 'Crimson',
        primary: { r: 0.9, g: 0.2, b: 0.3 },
        secondary: { r: 1.0, g: 0.7, b: 0.7 },
        accent: { r: 1.0, g: 0.5, b: 0.2 },
        dark: { r: 0.3, g: 0.05, b: 0.1 }
    },
    emerald: {
        id: 'emerald',
        name: 'Emerald',
        primary: { r: 0.2, g: 0.8, b: 0.4 },
        secondary: { r: 0.7, g: 1.0, b: 0.8 },
        accent: { r: 0.4, g: 1.0, b: 0.6 },
        dark: { r: 0.05, g: 0.2, b: 0.1 }
    },
    purple: {
        id: 'purple',
        name: 'Mystic',
        primary: { r: 0.6, g: 0.3, b: 0.9 },
        secondary: { r: 0.9, g: 0.8, b: 1.0 },
        accent: { r: 1.0, g: 0.4, b: 0.8 },
        dark: { r: 0.2, g: 0.1, b: 0.3 }
    },
    orange: {
        id: 'orange',
        name: 'Sunset',
        primary: { r: 1.0, g: 0.6, b: 0.2 },
        secondary: { r: 1.0, g: 0.9, b: 0.7 },
        accent: { r: 1.0, g: 0.3, b: 0.3 },
        dark: { r: 0.3, g: 0.15, b: 0.05 }
    },
    silver: {
        id: 'silver',
        name: 'Silver',
        primary: { r: 0.75, g: 0.75, b: 0.8 },
        secondary: { r: 0.95, g: 0.95, b: 1.0 },
        accent: { r: 0.5, g: 0.7, b: 0.9 },
        dark: { r: 0.2, g: 0.2, b: 0.25 }
    },
    midnight: {
        id: 'midnight',
        name: 'Midnight',
        primary: { r: 0.25, g: 0.25, b: 0.4 },
        secondary: { r: 0.45, g: 0.45, b: 0.6 },
        accent: { r: 0.6, g: 0.4, b: 0.9 },
        dark: { r: 0.1, g: 0.1, b: 0.2 }
    }
};

// Default customization for each player
export const DEFAULT_CUSTOMIZATION = {
    1: {
        style: 'creature',
        palette: 'golden',
        name: 'Player 1'
    },
    2: {
        style: 'creature',
        palette: 'blue',
        name: 'Player 2'
    }
};

// Get style config by ID
export function getStyleConfig(styleId) {
    return CHARACTER_STYLES[styleId] || CHARACTER_STYLES.creature;
}

// Get palette config by ID
export function getPaletteConfig(paletteId) {
    return COLOR_PALETTES[paletteId] || COLOR_PALETTES.golden;
}

// Get all style IDs
export function getStyleIds() {
    return Object.keys(CHARACTER_STYLES);
}

// Get all palette IDs
export function getPaletteIds() {
    return Object.keys(COLOR_PALETTES);
}
